import { useEffect, useState } from "react";
import { IPostcodeGroup } from "../../models/PostcodeGroup";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { NumberInput } from "../shared/Inputs/NumberInput";
import { TextInput } from "../shared/Inputs/TextInput";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import usePostcodeGroups from "../../data/usePostcodeGroups";
import { useForm } from "react-hook-form";
import SwitchInput from "../shared/Inputs/SwitchInput";
import useNotification from "../notifications/useNotifications";
import { NotificationVariant } from "../notifications/useNotifications";
import { validateFormFields, ValidationField } from "../../utils/formValidation";

type FormValues = {
  name: string,
  postcode: string,
  basePrice: number,
  onlyGoodEngineCondition: boolean,
  onlyGoodBodyCondition: boolean,
  thresholdPrice: number,
  isActive: boolean
};

export default function AddEditPostcodeGroup({ postcodeGroup, setOpen }: { postcodeGroup?: IPostcodeGroup | undefined, setOpen: any }) {
  const { deleteHandlers, saveHandlers } = useDefaultCRUDHandlers("Postcode Group");
  const { update, create, remove } = usePostcodeGroups();
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const { register, handleSubmit, reset, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      name: postcodeGroup?.name || "",
      postcode: postcodeGroup?.postcode || "",
      basePrice: postcodeGroup?.basePrice || 0,
      onlyGoodEngineCondition: postcodeGroup?.onlyGoodEngineCondition || false,
      onlyGoodBodyCondition: postcodeGroup?.onlyGoodBodyCondition || false,
      thresholdPrice: postcodeGroup?.thresholdPrice || 0,
      isActive: postcodeGroup?.isActive || true
    }
  });
  
  const { addNotification } = useNotification();

  const validationFields: ValidationField[] = [
    { field: 'name', label: 'Name' },
    { field: 'postcode', label: 'Postcode' },
    { field: 'basePrice', label: 'Base Price per Tonne', minValue: 0 },
    { field: 'thresholdPrice', label: 'Minimum Accepted Price', minValue: 0 }
  ];

  const onSubmit = (data: any) => {
    console.log("Form Data", data);

    data.thresholdPrice = data.thresholdPrice.toString();

    if (!validateFormFields(data, validationFields, addNotification)) {
      return;
    }

    const updatedScrapPrice = {
      ...postcodeGroup,
      ...data    
    };

    console.log("Updated Postcode Group", updatedScrapPrice);

    if (postcodeGroup !== undefined) {
      update.mutate(updatedScrapPrice, {
        onSuccess: (data) => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    } else {
      create.mutate(updatedScrapPrice, {
        onSuccess: (data) => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    }
  }

  const handleDelete = () => {
    remove.mutate(postcodeGroup?.id!, {
      onSuccess: (data) => {
        deleteHandlers.onSuccess();
        setOpen(false);
        setDeleteOpen(false);
        reset({});
      },
      onError: deleteHandlers.onError
    });
  }

  useEffect(() => {    
    reset({ ...postcodeGroup });
  }, [postcodeGroup]);

  // useEffect(() => {

  //   if (postcodeGroup !== undefined) {
  //     reset({ ...postcodeGroup });
  //   } else {
  //     reset({})
  //   }
  // }, [postcodeGroup, reset]);

  return (
    <>
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen}
        title={"Delete Postcode Group"} message={`Are you sure you want to delete this postcode group entry?\n This action cannot be undone.`}
        confirmButtonText={"Delete"} onConfirm={handleDelete} isLoading={false}
      />

      <h1 className="text-xl">{`${postcodeGroup ? "Edit" : "Add"} a Postcode Group`}</h1>
      <small className="text-gray-500"><i>
        {postcodeGroup
          ? "Edit the base price per tonne for leads from this postcode area"
          : "This will allow you to receive leads from this postcode area"}
      </i></small>
      <div className="mt-4">
        <form className="space-y-4 border-t border-gray-200" onSubmit={handleSubmit(onSubmit)} >
          <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-1">

            <TextInput label="Name" registerName="name" register={register} />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
              <TextInput disabled={!!postcodeGroup} label="Postcode Area (e.g. 'SW', 'PL')" registerName="postcode" register={register} />
              <NumberInput label="Base Price per Tonne (£)" registerName="basePrice" register={register} minVal={null} />
              <div className="sm:col-span-2">
                <NumberInput label={"Minimum Accepted Price"} register={register} registerName={"thresholdPrice"} minVal={null} />
              </div>         
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-4">
              <SwitchInput label="Only Good Engine" registerName="onlyGoodEngineCondition" register={register} watch={watch} setValue={setValue} />
              <SwitchInput label="Only Good Body" registerName="onlyGoodBodyCondition" register={register} watch={watch} setValue={setValue} />
              <SwitchInput label="Active" registerName="isActive" register={register} watch={watch} setValue={setValue} />
            </div>

            <ButtonInput label={`${postcodeGroup ? "Save Changes" : "Create"}`} isSubmit={true} onClick={undefined} classes={undefined} />
            {postcodeGroup !== undefined && (
              <ButtonInput label={"Delete"} isSubmit={false} onClick={() => (setDeleteOpen(true))} classes={"!bg-red-500 hover:!bg-red-600"} />
            )}
          </div>
        </form>
      </div>
    </>
  )

}